import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import { RevealOnScroll } from "../shared";
import { CLIENT_THESIS, VALUE_TRANSLATION, DELIVERY_LANES_SECTION, CLIENT_BENEFITS } from "./clientBenefitsContent";

const MetricChip = ({ label, variant }) => (
  <span
    className={`inline-block font-mono text-[11px] sm:text-xs px-2.5 py-1 rounded border ${
      variant === "client"
        ? "border-[#00D2FF]/35 text-partner-accent bg-[#00D2FF]/10"
        : "border-partner text-partner bg-[#080A0F]"
    }`}
  >
    {label}
  </span>
);

const DeliveryLane = ({ benefit, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.35, delay: index * 0.08 }}
    className="rounded-xl border border-[#2A3348] bg-[#11151F] px-5 py-5"
    data-testid={`client-lane-${benefit.id}`}
  >
    <div className="flex items-center justify-between gap-3 mb-3">
      <p className="font-mono text-[10px] sm:text-xs text-partner-accent font-semibold tracking-wider">
        {benefit.lane} · {benefit.code}
      </p>
      <span className="font-mono text-xs text-partner-accent/70 tabular-nums">
        {String(index + 1).padStart(2, "0")}
      </span>
    </div>

    <h3 className="text-base sm:text-lg font-bold text-partner leading-snug mb-4">
      {benefit.shortOutcome}
    </h3>

    <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-partner-muted uppercase tracking-wider mb-2">
          The gap
        </p>
        <p className="text-sm text-partner-muted font-body leading-relaxed">{benefit.problem}</p>
      </div>

      <div className="flex flex-col items-center justify-center lg:px-2 gap-1 flex-shrink-0">
        <div className="w-8 h-8 rounded-full border border-[#00D2FF]/35 bg-[#00D2FF]/10 flex items-center justify-center">
          <ArrowRight className="w-4 h-4 text-partner-accent rotate-90 lg:rotate-0" />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-partner-accent uppercase tracking-wider mb-2">
          The fix
        </p>
        <p className="text-sm text-partner font-body leading-relaxed">{benefit.solution}</p>
      </div>
    </div>
  </motion.div>
);

const ClientBenefitsSection = () => (
  <section
    className="section-padding section-alt relative overflow-hidden"
    data-testid="client-benefits"
  >
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <RevealOnScroll>
        <h2 className="text-3xl sm:text-4xl font-bold text-partner leading-tight mb-3 max-w-3xl">
          {CLIENT_THESIS.headline.split("Your End Clients")[0]}
          <span className="gradient-text">Your End Clients</span>
        </h2>
        <p className="text-partner-muted font-body max-w-2xl leading-relaxed mb-8">
          {CLIENT_THESIS.insight}
        </p>
      </RevealOnScroll>

      <RevealOnScroll delay={0.08}>
        <div
          className="rounded-xl border border-[#00D2FF]/25 bg-[#00D2FF]/5 px-5 py-5"
          data-testid="value-translation"
        >
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-partner-muted uppercase tracking-wider mb-2">
                {VALUE_TRANSLATION.agencyLabel}
              </p>
              <div className="flex flex-wrap gap-2">
                {VALUE_TRANSLATION.agencyMetrics.map((m) => (
                  <MetricChip key={m} label={m} variant="agency" />
                ))}
              </div>
            </div>

            <div className="flex flex-col items-center justify-center lg:px-2 gap-1">
              <div className="w-8 h-8 rounded-full border border-[#00D2FF]/35 bg-[#00D2FF]/10 flex items-center justify-center">
                <Plus className="w-4 h-4 text-partner-accent" />
              </div>
              <span className="font-mono text-[9px] text-partner-muted tracking-wider text-center max-w-[7rem]">
                {VALUE_TRANSLATION.connectorLabel}
              </span>
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-partner-accent uppercase tracking-wider mb-2">
                {VALUE_TRANSLATION.clientLabel}
              </p>
              <div className="flex flex-wrap gap-2">
                {VALUE_TRANSLATION.clientOutcomes.map((m) => (
                  <MetricChip key={m} label={m} variant="client" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </RevealOnScroll>

      <RevealOnScroll delay={0.12}>
        <div className="mt-10 pt-8 border-t border-[#2A3348]">
          <p className="text-xs font-semibold text-[#9AA8BE] uppercase tracking-wider mb-5 font-mono">
            {DELIVERY_LANES_SECTION.label}
          </p>
          <div className="flex flex-col gap-4" data-testid="client-delivery-lanes">
            {CLIENT_BENEFITS.map((benefit, index) => (
              <DeliveryLane key={benefit.id} benefit={benefit} index={index} />
            ))}
          </div>
        </div>
      </RevealOnScroll>
    </div>
  </section>
);

export default ClientBenefitsSection;
